/**
 * RefTagInput — referral タグの「自由記述 + プリセット選択」ハイブリッド入力。
 *
 * 1 つの value を扱うが、入力経路が 2 つ:
 *   1. 直接タイピング（自由記述）
 *   2. 登録済みチャネルプリセットから選択 → input にコピー
 *
 * バリデーション: REFERRAL_TAG_RE（半角英数 + _-. の 32 文字以内）
 *
 * 用途:
 *   - リフェラル画面 / URL 発行タブ
 *   - キャンペーンボードの referral カード設定モーダル
 */
import { useMemo } from 'react';

import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useReferralChannels } from '@/hooks/useReferralChannels';

import { REFERRAL_TAG_RE } from '@goenhub/shared';

const NONE = '__none__';

interface Props {
  value: string;
  /** 値が変わったとき呼ばれる。プリセット選択でも自由記述でも同じ */
  onChange: (refTag: string) => void;
  /** プリセット選択時に id を覚えておきたい場合（任意、UI 表示の継続用） */
  onPresetSelected?: (presetId: string | null) => void;
  /** ラベル（任意） */
  label?: string;
  /** プレースホルダ */
  placeholder?: string;
  /** id（label/htmlFor 関連付け用） */
  id?: string;
}

export function RefTagInput({
  value,
  onChange,
  onPresetSelected,
  label,
  placeholder = '32 文字以内・半角英数字 _ - .',
  id = 'reftag-input',
}: Props) {
  const channelsQ = useReferralChannels();
  const channels = useMemo(() => channelsQ.data ?? [], [channelsQ.data]);

  const valid = !value || REFERRAL_TAG_RE.test(value);
  // 入力中の値と一致するプリセットがあれば、Select の現在値として表示
  const matchedPresetId =
    channels.find((c) => c.refTag === value)?.id ?? '';

  const handlePresetPick = (presetId: string) => {
    if (presetId === NONE) {
      // 「（自由記述）」を選んでも値はクリアしない（テキスト入力を尊重）
      onPresetSelected?.(null);
      return;
    }
    const ch = channels.find((c) => c.id === presetId);
    if (!ch) return;
    onChange(ch.refTag);
    onPresetSelected?.(presetId);
  };

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <Input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          // 入力中の値が登録プリセット refTag と一致するならその id を継続表示
          const stillMatches = channels.find((c) => c.refTag === v)?.id ?? null;
          onPresetSelected?.(stillMatches);
        }}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="font-mono"
      />
      {value && !valid && (
        <p className="text-[11px] text-[var(--accent-amber)]">
          使える文字: 半角英数字、アンダースコア、ハイフン、ドット（32 文字以内）
        </p>
      )}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] flex-shrink-0">
          または
        </span>
        <Select
          value={matchedPresetId || NONE}
          onValueChange={handlePresetPick}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="プリセットから選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>（自由記述に戻す）</SelectItem>
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <span className="flex items-center gap-2">
                  <span>{c.name}</span>
                  <span className="font-mono text-[10px] text-[var(--text-muted)]">
                    {c.refTag}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {channels.length === 0 && (
        <p className="text-[10px] text-[var(--text-muted)] mt-1">
          プリセットを登録すると、ここから選択できます（リフェラル → チャネル管理）。
        </p>
      )}
    </div>
  );
}
